import { useParams } from "react-router";
import t from "@/lang/lang";
import {
  BaseDataPage,
  BaseDataPageContent,
  BaseDataPageDescription,
  BaseDataPageHeader,
  BaseDataPageTitle,
} from "@/routes/base-data/_components/layout/base-data-page";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { serviceKindLabel } from "@/services/backend/services/types/kinds";
import { parseOmniBaseDataServiceKind } from "./use-omni-base-data-service-kind";
import { AlertTriangle } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Alert403 } from "@/shared/components/alerts/alert-403";
import {
  TabsNav,
  TabsNavContent,
  TabsNavLink,
  TabsNavList,
} from "@/shared/components/layout/tabs-nav";

/**
 * OmniBaseDataRoute renders the base data pages of an omni document's
 * service kind given by the route parameter serviceKind, see
 * `omniBaseDataServiceKinds`. Its pages read the kind with
 * `useOmniBaseDataServiceKind`.
 */
export function OmniBaseDataRoute() {
  const params = useParams();
  const serviceKind = parseOmniBaseDataServiceKind(params.serviceKind);
  const canAccess = useFeatureAuth("gsm.omni");
  if (!canAccess) {
    return (
      <BaseDataPage>
        <Alert403 />
      </BaseDataPage>
    );
  }
  if (!serviceKind) {
    return (
      <BaseDataPage>
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>404</AlertTitle>
          <AlertDescription>
            {t("Für diese Leistungsart gibt es hier keine Basisdaten.")}
          </AlertDescription>
        </Alert>
      </BaseDataPage>
    );
  }
  const label = serviceKindLabel(serviceKind);

  return (
    <BaseDataPage>
      <BaseDataPageHeader>
        <BaseDataPageTitle>{label}</BaseDataPageTitle>
        <BaseDataPageDescription>
          {`${t("Hier können die Basisdaten für Dokumente der Leistungsart")} ${label} ${t("konfiguriert werden.")}`}
        </BaseDataPageDescription>
      </BaseDataPageHeader>
      <BaseDataPageContent>
        <TabsNav>
          <TabsNavList>
            <TabsNavLink to="offers">{t("Angebote")}</TabsNavLink>
            <TabsNavLink to="contracts">{t("Verträge")}</TabsNavLink>
            <TabsNavLink to="scope-of-works">
              {t("Leistungsverzeichnisse")}
            </TabsNavLink>
            <TabsNavLink to="positions">{t("Positionen")}</TabsNavLink>
            <TabsNavLink to="data-sheets">{t("Datenblätter")}</TabsNavLink>
          </TabsNavList>
          {/* The key resets the pages' state when the kind changes. */}
          <TabsNavContent
            key={serviceKind}
            className="pt-2"
            outletProps={{ context: serviceKind }}
          />
        </TabsNav>
      </BaseDataPageContent>
    </BaseDataPage>
  );
}
